import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ImageGallery from '../components/ImageGallery';
import socket from '../services/socket';
import AuctionRegistration from '../components/AuctionRegistration';
import LiveAuction from '../components/LiveAuction';
import API_URL from '../config/api';

const CarDetail = () => {
  const { id } = useParams();
  const { currentUser } = useAuth();
  const [car, setCar] = useState(null);
  const [auction, setAuction] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  // Základní načtení dat
  useEffect(() => {
    const fetchCarDetail = async () => {
      try {
        const [carResponse, auctionResponse] = await Promise.all([
          fetch(`${API_URL}/api/cars/${id}`),
          fetch(`${API_URL}/api/auctions/car/${id}`)
        ]);

        if (!carResponse.ok) throw new Error(`HTTP error! status: ${carResponse.status}`);

        const carData = await carResponse.json();
        setCar(carData);

        if (auctionResponse.ok) {
          const auctionData = await auctionResponse.json();
          setAuction(auctionData);
        }
      } catch (error) {
        setError(`Nepodařilo se načíst detail: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchCarDetail();
  }, [id]);

  // Socket.IO listeners pro aktualizace v reálném čase
  useEffect(() => {
    if (!auction?._id) return;

    // Připojení k místnosti aukce
    socket.emit('joinAuction', auction._id);

    // Poslech na změny příhozů
    socket.on('bidUpdated', (data) => {
      if (data.auctionId === auction._id) {
        setAuction(prev => ({
          ...prev,
          currentBid: data.amount,
          lastBidder: data.userId
        }));
      }
    });

    // Ostatní socket listenery...
    socket.on('auctionStatusChanged', (data) => {
      if (data.auctionId === auction._id) {
        setAuction(prev => ({
          ...prev,
          status: data.newStatus,
          countdownEndTime: data.countdownEndTime,
          auctionEndTime: data.auctionEndTime
        }));
      }
    });

    return () => {
      socket.emit('leaveAuction', auction._id);
      socket.off('bidUpdated');
      socket.off('auctionStatusChanged');
    };
  }, [auction?._id]);

  const handleBid = async (amount) => {
    if (!currentUser || !auction) return;

    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_URL}/api/auctions/${auction._id}/bid`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ amount })
      });

      if (!response.ok) throw new Error('Příhoz se nezdařil');
      
      const updatedAuction = await response.json();
      setAuction(updatedAuction);
    } catch (error) {
      throw new Error(error.message);
    }
  };

  const handleAuctionRegistration = async () => {
    if (!currentUser || !auction) return;

    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_URL}/api/auctions/${auction._id}/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Nepodařilo se zaregistrovat do aukce');
      }

      const updatedAuction = await response.json();
      setAuction(updatedAuction);
    } catch (error) {
      throw new Error(error.message);
    }
  };

  const handleStatusChange = (newStatus, data) => {
    console.log('Updating auction status to:', newStatus, data);
    setAuction(prev => ({
      ...prev,
      status: newStatus,
      countdownEndTime: data?.countdownEndTime,
      auctionEndTime: data?.auctionEndTime
    }));
  };

  // Funkce pro renderování správného komponentu aukce
  const renderAuctionComponent = () => {
    if (!auction) return null;

    console.log('Rendering auction component for status:', auction.status);

    switch (auction.status) {
      case 'live':
        return <LiveAuction 
          auction={auction}
          onStatusChange={handleStatusChange}
        />;
      case 'registration':
      case 'countdown':
        return <AuctionRegistration 
          auction={auction} 
          onRegister={handleAuctionRegistration}
          onStatusChange={handleStatusChange}
        />;
      case 'ended':
        return (
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-2xl font-bold mb-4">Aukce skončila</h2>
            <p className="text-gray-600">Tato aukce již byla ukončena.</p>
          </div>
        );
      default:
        return null;
    }
  };

  if (loading) return <div className="animate-spin">Načítání...</div>;
  if (error) return <div className="text-red-500">{error}</div>;
  if (!car) return <div>Auto nenalezeno</div>;

  return (
    <div className="min-h-screen bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto p-6 bg-gray-800 rounded-lg">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Levá strana - Informace o autě */}
          <div className="w-full md:w-1/2">
            <div className="text-white mb-4">
              <h1 className="text-3xl font-bold">{car.make} {car.model}</h1>
              <p className="text-lg">{car.year}</p>
            </div>

            <div className="mb-6">
              <ImageGallery images={car.images} />
            </div>

            {/* Základní informace */}
            <div className="grid grid-cols-2 gap-4 bg-gray-700 p-4 rounded-lg mb-4">
              <div>
                <p className="text-sm text-gray-400">Rok výroby</p>
                <p className="font-semibold text-white">{car.year}</p>
              </div>
              <div>
                <p className="text-sm text-gray-400">Najeto</p>
                <p className="font-semibold text-white">{car.mileage.toLocaleString()} km</p>
              </div>
              <div>
                <p className="text-sm text-gray-400">VIN</p>
                <p className="font-semibold text-white">{car.vin}</p>
              </div>
              <div>
                <p className="text-sm text-gray-400">Karosérie</p>
                <p className="font-semibold text-white">{car.bodyType}</p>
              </div>
            </div>

            {/* Technické specifikace */}
            <div className="bg-gray-700 p-4 rounded-lg mb-4">
              <h2 className="text-xl font-bold mb-4 text-white">Technické specifikace</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-400">Motor</p>
                  <p className="font-semibold text-white">{car.engineCapacity} ccm</p>
                  <p className="font-semibold text-white">{car.enginePower}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Palivo</p>
                  <p className="font-semibold text-white">{car.fuelType}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Převodovka</p>
                  <p className="font-semibold text-white">{car.transmission}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Pohon</p>
                  <p className="font-semibold text-white">{car.driveType || 'Neuvedeno'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Pravá strana - Aukce a další informace */}
          <div className="w-full md:w-1/2">
            <div className="mb-6">
              {renderAuctionComponent()}
            </div>

            {/* Další informace */}
            <div className="bg-gray-700 p-4 rounded-lg mb-4">
              <h2 className="text-xl font-bold mb-4 text-white">Další informace</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-400">Barva</p>
                  <p className="font-semibold text-white">{car.color}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Počet dveří</p>
                  <p className="font-semibold text-white">{car.numberOfDoors || 'Neuvedeno'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Počet míst</p>
                  <p className="font-semibold text-white">{car.seatingCapacity || 'Neuvedeno'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Umístění</p>
                  <p className="font-semibold text-white">{car.location}</p>
                </div>
              </div>
            </div>

            {/* CARFAX */}
            {car.carfax && (
              <div className="bg-gray-700 p-4 rounded-lg mb-4">
                <h2 className="text-xl font-bold mb-4 text-white">CARFAX Report</h2>
                <a 
                  href={car.carfax}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-500 hover:text-blue-400"
                >
                  Zobrazit CARFAX report
                </a>
              </div>
            )}

            {/* Popis */}
            <div className="bg-gray-700 p-4 rounded-lg mb-4">
              <h2 className="text-xl font-bold mb-4 text-white">Popis vozidla</h2>
              <p className="text-gray-300 whitespace-pre-wrap">{car.description}</p>
            </div>

            {/* Cena */}
            <div className="bg-gray-700 p-4 rounded-lg">
              <h2 className="text-xl font-bold mb-2 text-white">Vyvolávací cena</h2>
              <p className="text-2xl font-bold text-green-500">
                {car.startingPrice.toLocaleString()} Kč
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarDetail;